import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ApiService from '../services/apiService';
import { ExportConfiguration, ExportPushResult, MijnKantoorSearchResult, SummaryRef } from '../types';
import { logger } from '../utils/logger';

interface MijnKantoorExportFlowProps {
    fileId: string;
    summaryRef: SummaryRef;
    /** Alle MIJNKANTOOR-configuraties voor dit template */
    configs: ExportConfiguration[];
    summaryTitle: string;
    /** Terug naar het gewone deelmenu */
    onBack: () => void;
    /** Hele deel-dialoog sluiten */
    onClose: () => void;
}

// zoeken → klant kiezen (in de resultatenlijst) → bevestigen → link-call →
// resultaat. Alle stappen leven in dezelfde dialoog.
type FlowStep = 'search' | 'confirm' | 'linking' | 'success' | 'error';

// Spiegel van de backend-validatie: zoekterm (getrimd) minimaal 2 tekens.
const MIN_SEARCH_LENGTH = 2;

const MijnKantoorExportFlow: React.FC<MijnKantoorExportFlowProps> = ({
    fileId,
    summaryRef,
    configs,
    summaryTitle,
    onBack,
    onClose,
}) => {
    const { t } = useTranslation();
    const apiService = useMemo(() => new ApiService(), []);

    const [step, setStep] = useState<FlowStep>('search');
    const [selectedConfigId, setSelectedConfigId] = useState<string>(configs[0]?.id ?? '');
    const [searchTerm, setSearchTerm] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [searchError, setSearchError] = useState<string | null>(null);
    // Klantnamen uit het kantoordossier: uitsluitend in deze component-state
    // houden; niet persisten, niet loggen, niet doorgeven aan analytics.
    const [results, setResults] = useState<MijnKantoorSearchResult[]>([]);
    const [truncated, setTruncated] = useState(false);
    const [linkConfigId, setLinkConfigId] = useState<string>('');
    const [selectedResult, setSelectedResult] = useState<MijnKantoorSearchResult | null>(null);
    const [linkResult, setLinkResult] = useState<ExportPushResult | null>(null);

    const trimmedTerm = searchTerm.trim();
    const termTooShort = trimmedTerm.length < MIN_SEARCH_LENGTH;

    // Resultaten horen bij één configuratie (kantoor); bij wisselen opnieuw zoeken.
    const changeConfig = (configId: string) => {
        setSelectedConfigId(configId);
        setResults([]);
        setTruncated(false);
        setHasSearched(false);
        setSearchError(null);
    };

    const search = async () => {
        if (termTooShort || isSearching) return;
        setIsSearching(true);
        setSearchError(null);
        try {
            const response = await apiService.searchMijnKantoor(
                fileId,
                summaryRef,
                trimmedTerm,
                // config_id alleen meesturen als er echt iets te kiezen valt;
                // bij één configuratie lost de backend hem zelf op.
                configs.length > 1 ? selectedConfigId : undefined
            );
            setResults(response.results);
            setTruncated(response.truncated);
            setLinkConfigId(response.configId);
            setHasSearched(true);
        } catch (error: any) {
            // Alleen de foutstatus loggen; response-data kan klantgegevens bevatten.
            logger.error('Mijn Kantoor search failed with status:', error?.response?.status);
            const message = error?.response?.data?.error;
            setSearchError(message ? message.toString() : t('shareDialog.mijnkantoor.error'));
        } finally {
            setIsSearching(false);
        }
    };

    const link = async () => {
        if (!selectedResult) return;
        setStep('linking');
        try {
            const result = await apiService.linkMijnKantoor(
                fileId,
                summaryRef,
                linkConfigId || selectedConfigId,
                // Het klant-id letterlijk doorgeven: niet samenstellen, niet parsen.
                selectedResult.id
            );
            setLinkResult(result);
            setStep(result.success ? 'success' : 'error');
        } catch (error: any) {
            logger.error('Mijn Kantoor link failed with status:', error?.response?.status);
            const message = error?.response?.data?.error;
            setLinkResult({
                success: false,
                message: message ? message.toString() : t('shareDialog.mijnkantoor.error'),
            });
            setStep('error');
        }
    };

    const inputClass =
        'w-full px-3 py-2 text-sm rounded-lg border border-gray-300 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500';
    const primaryButtonClass =
        'px-4 py-2 text-sm rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed';
    const secondaryButtonClass =
        'px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors';

    // Persoon- of pand-icoon naast de klantnaam; business/internal/supplier
    // delen het pand.
    const typeIcon = (type?: string): React.ReactNode => {
        if (!type) return null;
        if (type === 'person') {
            return (
                <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                </svg>
            );
        }
        return (
            <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-2a1 1 0 00-1-1H9a1 1 0 00-1 1v2a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z" clipRule="evenodd" />
            </svg>
        );
    };

    if (step === 'search') {
        return (
            <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-900">
                    {t('shareDialog.mijnkantoor.searchTitle')}
                </h3>

                {configs.length > 1 && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t('shareDialog.mijnkantoor.configLabel')}
                        </label>
                        <select
                            value={selectedConfigId}
                            onChange={(e) => changeConfig(e.target.value)}
                            disabled={isSearching}
                            className={inputClass}
                        >
                            {configs.map((config) => (
                                <option key={config.id} value={config.id}>{config.name}</option>
                            ))}
                        </select>
                    </div>
                )}

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('shareDialog.mijnkantoor.searchLabel')}
                    </label>
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                search();
                            }
                        }}
                        disabled={isSearching}
                        autoFocus
                        className={inputClass}
                    />
                </div>

                {trimmedTerm.length > 0 && termTooShort && (
                    <p className="text-xs text-red-600">{t('shareDialog.mijnkantoor.searchMinChars')}</p>
                )}

                <div className="flex justify-end space-x-2">
                    <button onClick={onBack} disabled={isSearching} className={secondaryButtonClass}>
                        {t('shareDialog.mijnkantoor.back')}
                    </button>
                    <button
                        onClick={search}
                        disabled={termTooShort || isSearching}
                        className={primaryButtonClass}
                    >
                        {t('shareDialog.mijnkantoor.search')}
                    </button>
                </div>

                {isSearching && (
                    <div className="flex justify-center py-4">
                        <div className="w-5 h-5 border-2 border-gray-300 border-t-primary-600 rounded-full animate-spin"></div>
                    </div>
                )}

                {searchError && !isSearching && (
                    <div className="text-center space-y-2">
                        <p className="text-sm text-red-600">{searchError}</p>
                        <button onClick={search} className={secondaryButtonClass}>
                            {t('shareDialog.mijnkantoor.retry')}
                        </button>
                    </div>
                )}

                {hasSearched && !isSearching && !searchError && results.length === 0 && (
                    <p className="text-sm text-gray-500 text-center py-2">
                        {t('shareDialog.mijnkantoor.empty')}
                    </p>
                )}

                {hasSearched && !isSearching && !searchError && results.length > 0 && (
                    <div>
                        <div className="text-sm font-medium text-gray-700 mb-2">
                            {t('shareDialog.mijnkantoor.resultsLabel')}
                        </div>
                        {truncated && (
                            <div className="mb-2 px-3 py-2 text-xs rounded-lg bg-amber-50 border border-amber-200 text-amber-800">
                                {t('shareDialog.mijnkantoor.truncated')}
                            </div>
                        )}
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                            {results.map((result) => (
                                // De deeplink is een eigen <a> naast de rij-knop:
                                // interactieve elementen mogen niet in elkaar nesten.
                                <div key={result.id} className="flex items-center gap-2">
                                    <button
                                        onClick={() => {
                                            setSelectedResult(result);
                                            setStep('confirm');
                                        }}
                                        className="flex-1 min-w-0 flex items-center p-3 rounded-lg border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-colors text-left"
                                    >
                                        {result.type && (
                                            <div className="flex-shrink-0 text-gray-400 mr-3">
                                                {typeIcon(result.type)}
                                            </div>
                                        )}
                                        <div className="flex-1 min-w-0">
                                            <div className="font-medium text-gray-900 text-sm truncate">{result.name}</div>
                                            {(result.number || result.city) && (
                                                <div className="text-gray-500 text-xs truncate">
                                                    {[result.number, result.city].filter(Boolean).join(' · ')}
                                                </div>
                                            )}
                                        </div>
                                    </button>
                                    {result.webUrl && (
                                        <a
                                            href={result.webUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            title={t('shareDialog.mijnkantoor.openInPortal')}
                                            aria-label={t('shareDialog.mijnkantoor.openInPortal')}
                                            className="flex-shrink-0 p-2 rounded-lg text-gray-400 hover:text-primary-600 hover:bg-gray-50 transition-colors"
                                        >
                                            <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                                <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
                                                <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
                                            </svg>
                                        </a>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    }

    if (step === 'confirm' && selectedResult) {
        return (
            <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-900">
                    {t('shareDialog.mijnkantoor.searchTitle')}
                </h3>
                {/* Expliciete bevestiging: er wordt naar het klantdossier van
                    het kantoor geschreven. */}
                <p className="text-sm text-gray-900">
                    {t('shareDialog.mijnkantoor.confirm', {
                        title: summaryTitle,
                        name: selectedResult.name,
                    })}
                </p>
                {(selectedResult.number || selectedResult.city) && (
                    <div className="text-xs text-gray-500">
                        {[selectedResult.number, selectedResult.city].filter(Boolean).join(' · ')}
                    </div>
                )}
                <div className="flex justify-end space-x-2">
                    <button onClick={() => setStep('search')} className={secondaryButtonClass}>
                        {t('shareDialog.mijnkantoor.back')}
                    </button>
                    <button onClick={link} className={primaryButtonClass}>
                        {t('shareDialog.mijnkantoor.confirmAction')}
                    </button>
                </div>
            </div>
        );
    }

    if (step === 'linking') {
        return (
            <div className="flex flex-col items-center py-8 text-center">
                <div className="w-8 h-8 border-2 border-gray-300 border-t-primary-600 rounded-full animate-spin mb-4"></div>
                <div className="text-sm font-medium text-gray-900">{t('shareDialog.mijnkantoor.busy')}</div>
                <div className="text-xs text-gray-500 mt-1">{t('shareDialog.mijnkantoor.busyHint')}</div>
            </div>
        );
    }

    if (step === 'success') {
        return (
            <div className="flex flex-col items-center py-6 text-center">
                <svg className="w-8 h-8 text-primary-600 mb-3" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <p className="text-sm text-gray-900">
                    {linkResult?.message || t('shareDialog.mijnkantoor.success')}
                </p>
                <button onClick={onClose} className={`${secondaryButtonClass} mt-4`}>
                    {t('shareDialog.mijnkantoor.close')}
                </button>
            </div>
        );
    }

    // step === 'error' (of confirm zonder selectie, wat niet voor hoort te komen)
    return (
        <div className="flex flex-col items-center py-6 text-center">
            <svg className="w-8 h-8 text-red-600 mb-3" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <p className="text-sm text-gray-900">
                {linkResult?.message || t('shareDialog.mijnkantoor.error')}
            </p>
            <div className="flex space-x-2 mt-4">
                {/* Geen automatische retries: opnieuw proberen is een bewuste
                    actie, anders kan de notitie dubbel landen. */}
                <button onClick={link} className={primaryButtonClass}>
                    {t('shareDialog.mijnkantoor.retry')}
                </button>
                <button onClick={() => setStep('search')} className={secondaryButtonClass}>
                    {t('shareDialog.mijnkantoor.back')}
                </button>
            </div>
        </div>
    );
};

export default MijnKantoorExportFlow;
